import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAY_NAMES = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const NTH_OPTIONS = [
  { value: 1, label: 'First' },
  { value: 2, label: 'Second' },
  { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' },
  { value: -1, label: 'Last' },
  { value: -2, label: 'Second to last' },
];

const COUNTRY_LIST = [
  { code: 'GB', name: 'United Kingdom' }, { code: 'ES', name: 'Spain' },
  { code: 'US', name: 'United States' }, { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' }, { code: 'IT', name: 'Italy' },
  { code: 'PT', name: 'Portugal' }, { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' }, { code: 'IE', name: 'Ireland' },
  { code: 'AT', name: 'Austria' }, { code: 'CH', name: 'Switzerland' },
  { code: 'SE', name: 'Sweden' }, { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' }, { code: 'FI', name: 'Finland' },
  { code: 'PL', name: 'Poland' }, { code: 'CZ', name: 'Czech Republic' },
  { code: 'GR', name: 'Greece' }, { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' }, { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' }, { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' }, { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' }, { code: 'AR', name: 'Argentina' },
  { code: 'ZA', name: 'South Africa' }, { code: 'AE', name: 'UAE' },
  { code: 'SG', name: 'Singapore' }, { code: 'HK', name: 'Hong Kong' },
  { code: 'NZ', name: 'New Zealand' }, { code: 'TR', name: 'Turkey' },
];

export default function HolidayCustomization({ year, countries, onRefresh, onCountryAdd, onCountryRemove }) {
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [customHolidays, setCustomHolidays] = useState([]);
  const [excludedCount, setExcludedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPublic, setShowPublic] = useState(true);
  const [showCustom, setShowCustom] = useState(true);
  const [showExcluded, setShowExcluded] = useState(false);
  const [excludedHolidays, setExcludedHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const getCountryInfo = (code) => COUNTRY_LIST.find(c => c.code === code);

  const availableCountries = COUNTRY_LIST.filter(c =>
    !countries.some(uc => uc.country_code === c.code) &&
    (searchTerm === '' || c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     c.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const loadEffective = useCallback(async () => {
    if (!countries || countries.length === 0) {
      setPublicHolidays([]); setCustomHolidays([]); setExcludedCount(0);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/timeoff/holidays/effective/${year}`);
      setPublicHolidays(res.data.public_holidays || []);
      setCustomHolidays(res.data.custom_holidays || []);
      setExcludedCount(res.data.excluded_count || 0);
    } catch (err) {
      console.error('Failed to load holidays:', err);
    } finally {
      setLoading(false);
    }
  }, [year, countries]);

  const loadExcluded = useCallback(async () => {
    try {
      const res = await api.get(`/timeoff/holidays/excluded?year=${year}`);
      setExcludedHolidays(res.data || []);
    } catch (err) { console.error('Failed to load excluded:', err); }
  }, [year]);

  useEffect(() => { loadEffective(); }, [loadEffective]);
  useEffect(() => { if (showExcluded) loadExcluded(); }, [showExcluded, loadExcluded]);

  const handleExclude = async (holiday) => {
    try {
      await api.post('/timeoff/holidays/excluded', {
        holiday_date: holiday.date, holiday_name: holiday.name, country_code: holiday.country_code
      });
      loadEffective();
      if (showExcluded) loadExcluded();
      if (onRefresh) onRefresh();
    } catch (err) { console.error('Failed to exclude:', err); }
  };

  const handleRestore = async (id) => {
    try {
      await api.delete(`/timeoff/holidays/excluded/${id}`);
      loadEffective(); loadExcluded();
      if (onRefresh) onRefresh();
    } catch (err) { console.error('Failed to restore:', err); }
  };

  const handleDeleteCustom = async (id) => {
    if (!window.confirm('Delete this custom holiday?')) return;
    try {
      await api.delete(`/timeoff/holidays/custom/${id}`);
      loadEffective();
      if (onRefresh) onRefresh();
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const handleSaveCustom = async (data) => {
    try {
      if (editingHoliday) {
        await api.put(`/timeoff/holidays/custom/${editingHoliday.id}`, data);
      } else {
        await api.post('/timeoff/holidays/custom', data);
      }
      setShowModal(false); setEditingHoliday(null);
      loadEffective();
      if (onRefresh) onRefresh();
    } catch (err) { console.error('Failed to save custom holiday:', err); }
  };

  // Group public holidays by month
  const groupedPublic = publicHolidays.reduce((acc, h) => {
    const m = parseInt(h.date.split('-')[1]) - 1;
    if (!acc[m]) acc[m] = [];
    acc[m].push(h);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    return `${days[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0,3)}`;
  };

  return (
    <div className="settings-section holiday-customization">
      <h3>🌍 Public Holidays</h3>
      <p className="section-description">
        Select countries, exclude holidays that don't apply, or add custom ones.
      </p>

      {/* Country tags */}
      <div className="countries-tags">
        {countries.map(c => {
          const info = getCountryInfo(c.country_code);
          return (
            <div key={c.id} className="country-tag" style={{ borderColor: c.color || '#4f46e5' }}>
              <span className="country-code-label">{c.country_code}</span>
              <span className="country-name">{info?.name || c.country_code}</span>
              {c.is_primary && <span className="primary-badge">PRIMARY</span>}
              <button className="tag-remove" onClick={() => onCountryRemove(c.id)}>×</button>
            </div>
          );
        })}
      </div>

      {/* Country dropdown */}
      <div className="country-dropdown-wrap">
        <div className="country-search-input" onClick={() => setShowDropdown(!showDropdown)}>
          <input type="text" placeholder="Search and select countries..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
          />
          <span className="dropdown-caret">{showDropdown ? '▲' : '▼'}</span>
        </div>
        {showDropdown && (
          <>
            <div className="country-dropdown-list">
              {availableCountries.length === 0 ? (
                <div className="dropdown-empty">No countries found</div>
              ) : availableCountries.map(c => (
                <div key={c.code} className="dropdown-item"
                  onClick={() => { onCountryAdd(c.code); setSearchTerm(''); setShowDropdown(false); }}>
                  <span className="item-name">{c.name}</span>
                  <span className="item-code">{c.code}</span>
                </div>
              ))}
            </div>
            <div className="dropdown-backdrop" onClick={() => setShowDropdown(false)} />
          </>
        )}
      </div>

      {/* Public holidays */}
      {countries.length > 0 && (
        <div className="holiday-group">
          <div className="holiday-group-header" onClick={() => setShowPublic(!showPublic)}>
            <span>🏛 Public Holidays ({publicHolidays.length})</span>
            <span className="collapse-arrow">{showPublic ? '▾' : '▸'}</span>
          </div>
          {showPublic && (
            <div className="holiday-group-body">
              {loading ? (
                <p className="holiday-loading">Loading holidays...</p>
              ) : publicHolidays.length === 0 ? (
                <p className="holiday-empty">No public holidays found for {year}.</p>
              ) : (
                Object.keys(groupedPublic).sort((a,b) => a - b).map(mi => (
                  <div key={mi} className="holiday-month-group">
                    <div className="holiday-month-label">{MONTH_NAMES[mi]}</div>
                    {groupedPublic[mi].map((h, i) => (
                      <div key={i} className="holiday-row">
                        <span className="holiday-color-dot" style={{ backgroundColor: h.color || '#6366f1' }} />
                        <span className="holiday-date">{formatDate(h.date)}</span>
                        <span className="holiday-name">{h.name}</span>
                        <span className="holiday-country-badge">{h.country_code}</span>
                        <button className="btn-exclude" title="Exclude" onClick={() => handleExclude(h)}>✕</button>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Excluded holidays */}
      {excludedCount > 0 && (
        <div className="holiday-group excluded-group">
          <div className="holiday-group-header" onClick={() => setShowExcluded(!showExcluded)}>
            <span>🚫 Excluded ({excludedCount})</span>
            <span className="collapse-arrow">{showExcluded ? '▾' : '▸'}</span>
          </div>
          {showExcluded && (
            <div className="holiday-group-body">
              {excludedHolidays.map(h => (
                <div key={h.id} className="holiday-row excluded-row">
                  <span className="holiday-date">{formatDate(h.holiday_date)}</span>
                  <span className="holiday-name">{h.holiday_name}</span>
                  <span className="holiday-country-badge">{h.country_code}</span>
                  <button className="btn-restore" title="Restore" onClick={() => handleRestore(h.id)}>↩</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Custom holidays */}
      <div className="holiday-group">
        <div className="holiday-group-header" onClick={() => setShowCustom(!showCustom)}>
          <span>⭐ Custom Holidays ({customHolidays.length})</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="collapse-arrow">{showCustom ? '▾' : '▸'}</span>
            <button className="btn-add-small" onClick={(e) => {
              e.stopPropagation(); setEditingHoliday(null); setShowModal(true);
            }}>+ Add</button>
          </div>
        </div>

        {showCustom && (
          <div className="holiday-group-body">
            {customHolidays.length === 0 ? (
              <p className="holiday-empty">No custom holidays yet.</p>
            ) : customHolidays.map(h => (
              <div key={h.id} className="holiday-row custom-row">
                <span className="holiday-color-dot" style={{ backgroundColor: h.color || '#f59e0b' }} />
                <span className="holiday-date">{formatDate(h.resolved_date)}</span>
                <span className="holiday-name">{h.name}</span>
                {h.rule_description && <span className="rule-desc">{h.rule_description}</span>}
                <div className="holiday-actions">
                  <button className="btn-edit-sm" onClick={() => { setEditingHoliday(h); setShowModal(true); }}>✎</button>
                  <button className="btn-delete-sm" onClick={() => handleDeleteCustom(h.id)}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <CustomHolidayModal
          holiday={editingHoliday}
          year={year}
          onSave={handleSaveCustom}
          onClose={() => { setShowModal(false); setEditingHoliday(null); }}
        />
      )}
    </div>
  );
}

function CustomHolidayModal({ holiday, year, onSave, onClose }) {
  const PRESET_COLORS = ['#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#6b7280'];
  const [name, setName] = useState(holiday?.name || '');
  const [color, setColor] = useState(holiday?.color || '#f59e0b');
  const [ruleType, setRuleType] = useState(holiday?.rule_type || 'fixed_date');
  const [month, setMonth] = useState(holiday?.month || 1);
  const [day, setDay] = useState(holiday?.day || 1);
  const [nthWeek, setNthWeek] = useState(holiday?.nth || 1);
  const [weekday, setWeekday] = useState(holiday?.weekday || 0);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const data = { name: name.trim(), color, rule_type: ruleType, month };
    if (ruleType === 'fixed_date') {
      data.day = day;
    } else {
      data.nth = nthWeek;
      data.weekday = weekday;
    }
    try {
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  // Preview: compute date for current year
  const previewDate = (() => {
    try {
      if (ruleType === 'fixed_date') {
        const d = new Date(year, month - 1, day);
        if (d.getMonth() !== month - 1) return null;
        return d;
      } else {
        // nth weekday of month
        const firstDay = new Date(year, month - 1, 1);
        if (nthWeek > 0) {
          let count = 0;
          for (let d = 1; d <= 31; d++) {
            const dt = new Date(year, month - 1, d);
            if (dt.getMonth() !== month - 1) break;
            if (dt.getDay() === ((weekday + 1) % 7)) { // weekday: 0=Mon
              count++;
              if (count === nthWeek) return dt;
            }
          }
        } else {
          // Last or second-to-last
          const lastDay = new Date(year, month, 0).getDate();
          let found = [];
          for (let d = lastDay; d >= 1; d--) {
            const dt = new Date(year, month - 1, d);
            if (dt.getDay() === ((weekday + 1) % 7)) found.push(dt);
          }
          const idx = Math.abs(nthWeek) - 1;
          return found[idx] || null;
        }
      }
    } catch { return null; }
    return null;
  })();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h3>{holiday ? 'Edit Custom Holiday' : 'Add Custom Holiday'}</h3>

        <div className="form-group">
          <label>Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Company Day Off" />
        </div>

        <div className="form-group">
          <label>Color</label>
          <div className="color-picker-row">
            {PRESET_COLORS.map(c => (
              <button key={c} className={`color-swatch ${color === c ? 'active' : ''}`}
                style={{ backgroundColor: c }} onClick={() => setColor(c)} />
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Rule Type</label>
          <select value={ruleType} onChange={e => setRuleType(e.target.value)}>
            <option value="fixed_date">Fixed date (e.g. Dec 25)</option>
            <option value="nth_weekday">Nth weekday (e.g. 3rd Monday of Jan)</option>
          </select>
        </div>

        <div className="form-group">
          <label>Month</label>
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))}>
            {MONTH_NAMES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
        </div>

        {ruleType === 'fixed_date' ? (
          <div className="form-group">
            <label>Day</label>
            <input type="number" min="1" max="31" value={day}
              onChange={e => setDay(parseInt(e.target.value) || 1)} />
          </div>
        ) : (
          <>
            <div className="form-group">
              <label>Which</label>
              <select value={nthWeek} onChange={e => setNthWeek(parseInt(e.target.value))}>
                {NTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Weekday</label>
              <select value={weekday} onChange={e => setWeekday(parseInt(e.target.value))}>
                {WEEKDAY_NAMES.map((w, i) => <option key={i} value={i}>{w}</option>)}
              </select>
            </div>
          </>
        )}

        {previewDate && (
          <div className="holiday-preview">
            📅 In {year}: {previewDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : (holiday ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}
